import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash, Search, Check, X, Filter, DollarSign, Calendar, Package, User, Upload, Image as ImageIcon, Eye, Settings } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const ThuChiTheoLoManagement = () => {
    // State Management
    const [thuChiList, setThuChiList] = useState([]);
    const [caiDatThuChiList, setCaiDatThuChiList] = useState([]);
    const [loList, setLoList] = useState([]);
    const [nhanVienList, setNhanVienList] = useState([]);
    const [currentThuChi, setCurrentThuChi] = useState({
        'ID': '',
        'Ngày': '',
        'Nghiệp vụ': '',
        'Hạng mục': '',
        'Số lô': '',
        'Số tiền': '',
        'Người giao': '',
        'Hình ảnh UNC': '',
        'Ghi chú': ''
    });
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [dateFilterFrom, setDateFilterFrom] = useState('');
    const [dateFilterTo, setDateFilterTo] = useState('');
    const [nghiepVuFilter, setNghiepVuFilter] = useState('');
    const [loFilter, setLoFilter] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [searchNhanVien, setSearchNhanVien] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [imageToView, setImageToView] = useState(null);
    const dropZoneRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    // Cài đặt thu chi
    const [showCaiDatModal, setShowCaiDatModal] = useState(false);
    const [currentCaiDat, setCurrentCaiDat] = useState({
        'Nghiệp vụ': '',
        'Hạng mục': ''
    });
    const [isEditCaiDat, setIsEditCaiDat] = useState(false);
    const [showDeleteCaiDatConfirmation, setShowDeleteCaiDatConfirmation] = useState(false);
    const [caiDatToDelete, setCaiDatToDelete] = useState(null);

    // Search Số lô
    const [searchLo, setSearchLo] = useState('');
    const [showLoDropdown, setShowLoDropdown] = useState(false);

    // Fetch data functions
    const fetchThuChiList = async () => {
        try {
            const response = await authUtils.apiRequest('THUCHI_THEOLO', 'Find', {});
            setThuChiList(response);
        } catch (error) {
            console.error('Error fetching thu chi list:', error);
            toast.error('Lỗi khi tải danh sách thu chi');
        }
    };

    const fetchCaiDatThuChi = async () => {
        try {
            const response = await authUtils.apiRequest('CAIDATTHUCHI_LO', 'Find', {});
            setCaiDatThuChiList(response);
        } catch (error) {
            console.error('Error fetching cai dat thu chi:', error);
            toast.error('Lỗi khi tải cài đặt thu chi');
        }
    };

    const fetchLoList = async () => {
        try {
            const response = await authUtils.apiRequest('QUANLYLO', 'Find', {});
            setLoList(response);
        } catch (error) {
            console.error('Error fetching lo list:', error);
            toast.error('Lỗi khi tải danh sách lô');
        }
    };

    const fetchNhanVienList = async () => {
        try {
            const response = await authUtils.apiRequest('DSNV', 'Find', {});
            setNhanVienList(response);
        } catch (error) {
            console.error('Error fetching nhan vien list:', error);
            toast.error('Lỗi khi tải danh sách nhân viên');
        }
    };

    useEffect(() => {
        fetchThuChiList();
        fetchCaiDatThuChi();
        fetchLoList();
        fetchNhanVienList();
    }, []);

    // Tạo ID tự động
    const generateID = () => {
        if (thuChiList.length === 0) return 'TC001';

        const maxId = thuChiList.reduce((max, item) => {
            const idNum = parseInt(item.ID.replace('TC', ''));
            return idNum > max ? idNum : max;
        }, 0);

        return `TC${String(maxId + 1).padStart(3, '0')}`;
    };

    // Get hạng mục theo nghiệp vụ
    const getHangMucByNghiepVu = (nghiepVu) => {
        return caiDatThuChiList
            .filter(item => item['Nghiệp vụ'] === nghiepVu)
            .map(item => item['Hạng mục']);
    };

    // Lọc nhân viên theo từ khóa tìm kiếm
    const filteredNhanVien = nhanVienList.filter(nv =>
        nv['Họ và Tên'].toLowerCase().includes(searchNhanVien.toLowerCase())
    );

    // Lọc số lô theo từ khóa tìm kiếm
    const filteredLo = loList.filter(lo =>
        lo['Số lô'].toLowerCase().includes(searchLo.toLowerCase()) ||
        (lo['Nhà cung cấp'] && lo['Nhà cung cấp'].toLowerCase().includes(searchLo.toLowerCase()))
    );

    // Format số tiền Việt Nam
    const formatCurrency = (value) => {
        if (!value) return '';
        // Remove all non-digit characters
        const numericValue = value.toString().replace(/\D/g, '');
        // Format with thousand separators
        return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    // Parse currency to number
    const parseCurrency = (value) => {
        if (!value) return '';
        return value.toString().replace(/\./g, '');
    };

    // Image upload handlers
    const handleImageUpload = async (file) => {
        if (!file) return;

        try {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);

            const toastId = toast.loading("Đang tải ảnh lên...");

            const result = await authUtils.uploadImage(file);

            if (result && result.success && result.url) {
                handleInputChange('Hình ảnh UNC', result.url);
                setSelectedImage(result.url);
                toast.update(toastId, {
                    render: "Tải ảnh thành công",
                    type: "success",
                    isLoading: false,
                    autoClose: 2000
                });
            } else {
                throw new Error("Không thể lấy URL ảnh");
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Lỗi khi tải ảnh: ' + error.message);
            setSelectedImage(null);
        }
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            const validation = authUtils.validateImage(file);

            if (!validation.isValid) {
                toast.error(validation.errors.join('\n'));
                return;
            }

            setCurrentThuChi(prev => ({
                ...prev,
                'FILE_IMAGE': file
            }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);

            if (authUtils.shouldUploadImmediately) {
                handleImageUpload(file);
            }
        }
    };

    const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const validation = authUtils.validateImage(file);

            if (!validation.isValid) {
                toast.error(validation.errors.join('\n'));
                return;
            }

            setCurrentThuChi(prev => ({
                ...prev,
                'FILE_IMAGE': file
            }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Modal handlers
    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEditMode(true);
            setCurrentThuChi({
                'ID': item['ID'] || '',
                'Ngày': item['Ngày'] || '',
                'Nghiệp vụ': item['Nghiệp vụ'] || '',
                'Hạng mục': item['Hạng mục'] || '',
                'Số lô': item['Số lô'] || '',
                'Số tiền': item['Số tiền'] || '',
                'Người giao': item['Người giao'] || '',
                'Hình ảnh UNC': item['Hình ảnh UNC'] || '',
                'Ghi chú': item['Ghi chú'] || ''
            });
            setSelectedImage(item['Hình ảnh UNC'] || null);
            setSearchLo(item['Số lô'] || '');
        } else {
            setIsEditMode(false);
            const today = new Date().toISOString().split('T')[0];
            setCurrentThuChi({
                'ID': generateID(),
                'Ngày': today,
                'Nghiệp vụ': '',
                'Hạng mục': '',
                'Số lô': '',
                'Số tiền': '',
                'Người giao': '',
                'Hình ảnh UNC': '',
                'Ghi chú': ''
            });
            setSelectedImage(null);
            setSearchLo('');
        }
        setSearchNhanVien('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setSelectedImage(null);
        setSearchNhanVien('');
        setSearchLo('');
        setShowLoDropdown(false);
        setCurrentThuChi({
            'ID': '',
            'Ngày': '',
            'Nghiệp vụ': '',
            'Hạng mục': '',
            'Số lô': '',
            'Số tiền': '',
            'Người giao': '',
            'Hình ảnh UNC': '',
            'Ghi chú': ''
        });
    };

    // Format date cho input type="date" (yyyy-mm-dd)
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Handle nghiệp vụ change - reset hạng mục
    const handleNghiepVuChange = (nghiepVu) => {
        setCurrentThuChi(prev => ({
            ...prev,
            'Nghiệp vụ': nghiepVu,
            'Hạng mục': '' // Reset hạng mục khi đổi nghiệp vụ
        }));
    };

    const handleInputChange = (field, value) => {
        setCurrentThuChi(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle số tiền change with formatting
    const handleSoTienChange = (value) => {
        const numericValue = parseCurrency(value);
        handleInputChange('Số tiền', numericValue);
    };

    // Handle số lô selection
    const handleLoSelect = (soLo) => {
        setSearchLo(soLo);
        handleInputChange('Số lô', soLo);
        setShowLoDropdown(false);
    };

    // Validation
    const validateThuChi = (item) => {
        const errors = [];
        if (!item['Ngày']) errors.push('Ngày không được để trống');
        if (!item['Nghiệp vụ']) errors.push('Nghiệp vụ không được để trống');
        if (!item['Hạng mục']) errors.push('Hạng mục không được để trống');
        if (!item['Số lô']) errors.push('Số lô không được để trống');
        if (!item['Số tiền']) errors.push('Số tiền không được để trống');
        if (!item['Người giao']) errors.push('Người giao không được để trống');

        const soTien = parseFloat(item['Số tiền']);
        if (isNaN(soTien) || soTien <= 0) {
            errors.push('Số tiền phải là số dương');
        }

        return errors;
    };

    // Save handler
    const handleSave = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const errors = validateThuChi(currentThuChi);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            let thuChiToSave = { ...currentThuChi };

            // If there's a new image file, upload to server
            if (thuChiToSave['FILE_IMAGE']) {
                try {
                    const uploadResult = await authUtils.uploadImage(thuChiToSave['FILE_IMAGE']);
                    if (uploadResult && uploadResult.success && uploadResult.url) {
                        thuChiToSave['Hình ảnh UNC'] = uploadResult.url;
                    } else {
                        throw new Error("Không thể lấy URL ảnh");
                    }
                } catch (error) {
                    toast.error('Lỗi khi tải ảnh: ' + error.message);
                    setIsSubmitting(false);
                    return;
                }
            }

            // Remove FILE_IMAGE field before sending to API
            delete thuChiToSave['FILE_IMAGE'];

            if (isEditMode) {
                await authUtils.apiRequest('THUCHI_THEOLO', 'Edit', {
                    "Rows": [thuChiToSave]
                });
                toast.success('Cập nhật thu chi thành công!');
            } else {
                await authUtils.apiRequest('THUCHI_THEOLO', 'Add', {
                    "Rows": [thuChiToSave]
                });
                toast.success('Thêm thu chi mới thành công!');
            }

            await fetchThuChiList();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving thu chi:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu thu chi'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete handlers
    const handleOpenDeleteConfirmation = (item) => {
        setItemToDelete(item);
        setShowDeleteConfirmation(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setItemToDelete(null);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;

        try {
            await authUtils.apiRequest('THUCHI_THEOLO', 'Delete', {
                "Rows": [{
                    "ID": itemToDelete['ID']
                }]
            });
            toast.success('Xóa thu chi thành công!');
            await fetchThuChiList();
            handleCloseDeleteConfirmation();
        } catch (error) {
            console.error('Error deleting thu chi:', error);
            toast.error('Có lỗi xảy ra khi xóa thu chi');
        }
    };

    // Image view modal
    const handleViewImage = (imageUrl) => {
        setImageToView(imageUrl);
        setShowImageModal(true);
    };

    const handleCloseImageModal = () => {
        setShowImageModal(false);
        setImageToView(null);
    };

    // Cài đặt thu chi handlers
    const handleOpenCaiDatModal = (item = null) => {
        if (item) {
            setIsEditCaiDat(true);
            setCurrentCaiDat({
                'Nghiệp vụ': item['Nghiệp vụ'] || '',
                'Hạng mục': item['Hạng mục'] || ''
            });
        } else {
            setIsEditCaiDat(false);
            setCurrentCaiDat({
                'Nghiệp vụ': '',
                'Hạng mục': ''
            });
        }
        setShowCaiDatModal(true);
    };

    const handleCloseCaiDatModal = () => {
        setShowCaiDatModal(false);
        setIsEditCaiDat(false);
        setCurrentCaiDat({
            'Nghiệp vụ': '',
            'Hạng mục': ''
        });
    };

    const validateCaiDat = (item) => {
        const errors = [];
        if (!item['Nghiệp vụ']) errors.push('Nghiệp vụ không được để trống');
        if (!item['Hạng mục']) errors.push('Hạng mục không được để trống');
        return errors;
    };

    const handleSaveCaiDat = async () => {
        try {
            const errors = validateCaiDat(currentCaiDat);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                return;
            }

            if (isEditCaiDat) {
                await authUtils.apiRequest('CAIDATTHUCHI_LO', 'Edit', {
                    "Rows": [currentCaiDat]
                });
                toast.success('Cập nhật cài đặt thành công!');
            } else {
                // Check if already exists
                const exists = caiDatThuChiList.some(
                    item => item['Hạng mục'] === currentCaiDat['Hạng mục']
                );
                if (exists) {
                    toast.error('Hạng mục này đã tồn tại!');
                    return;
                }

                await authUtils.apiRequest('CAIDATTHUCHI_LO', 'Add', {
                    "Rows": [currentCaiDat]
                });
                toast.success('Thêm cài đặt mới thành công!');
            }

            await fetchCaiDatThuChi();
            handleCloseCaiDatModal();
        } catch (error) {
            console.error('Error saving cai dat:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu cài đặt'));
        }
    };

    const handleOpenDeleteCaiDatConfirmation = (item) => {
        setCaiDatToDelete(item);
        setShowDeleteCaiDatConfirmation(true);
    };

    const handleCloseDeleteCaiDatConfirmation = () => {
        setShowDeleteCaiDatConfirmation(false);
        setCaiDatToDelete(null);
    };

    const handleDeleteCaiDat = async () => {
        if (!caiDatToDelete) return;

        try {
            await authUtils.apiRequest('CAIDATTHUCHI_LO', 'Delete', {
                "Rows": [{
                    "Hạng mục": caiDatToDelete['Hạng mục']
                }]
            });
            toast.success('Xóa cài đặt thành công!');
            await fetchCaiDatThuChi();
            handleCloseDeleteCaiDatConfirmation();
        } catch (error) {
            console.error('Error deleting cai dat:', error);
            toast.error('Có lỗi xảy ra khi xóa cài đặt');
        }
    };

    // Sorting
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortedItems = useCallback(() => {
        const sortableItems = [...thuChiList];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const keyA = a[sortConfig.key];
                const keyB = b[sortConfig.key];

                if (sortConfig.key === 'Số tiền') {
                    const numA = parseFloat(keyA) || 0;
                    const numB = parseFloat(keyB) || 0;
                    if (numA < numB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (numA > numB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                } else if (sortConfig.key === 'Ngày') {
                    const dateA = new Date(keyA || '1900-01-01');
                    const dateB = new Date(keyB || '1900-01-01');
                    if (dateA < dateB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (dateA > dateB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                } else {
                    if (keyA < keyB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (keyA > keyB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                }
            });
        }
        return sortableItems;
    }, [thuChiList, sortConfig]);

    // Filtering
    const filteredItems = getSortedItems().filter(item => {
        const matchesSearch = (
            item['ID']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Nghiệp vụ']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Hạng mục']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Số lô']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Người giao']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Ghi chú']?.toLowerCase().includes(search.toLowerCase())
        );

        // Date range filter
        let matchesDateFilter = true;
        if (dateFilterFrom || dateFilterTo) {
            const itemDate = new Date(item['Ngày']);
            if (dateFilterFrom) {
                const fromDate = new Date(dateFilterFrom);
                matchesDateFilter = matchesDateFilter && itemDate >= fromDate;
            }
            if (dateFilterTo) {
                const toDate = new Date(dateFilterTo);
                toDate.setHours(23, 59, 59, 999); // Include the entire end date
                matchesDateFilter = matchesDateFilter && itemDate <= toDate;
            }
        }

        const matchesNghiepVuFilter = !nghiepVuFilter ||
            item['Nghiệp vụ'] === nghiepVuFilter;

        const matchesLoFilter = !loFilter ||
            item['Số lô']?.toLowerCase().includes(loFilter.toLowerCase());

        return matchesSearch && matchesDateFilter && matchesNghiepVuFilter && matchesLoFilter;
    });

    // Get unique values for filters
    const uniqueNghiepVu = [...new Set(caiDatThuChiList.map(item => item['Nghiệp vụ']).filter(Boolean))];
    const uniqueLo = [...new Set(loList.map(item => item['Số lô']).filter(Boolean))];

    // Calculate statistics based on filtered items
    const tongThu = filteredItems
        .filter(item => item['Nghiệp vụ'] === 'Thu')
        .reduce((sum, item) => sum + (parseFloat(item['Số tiền']) || 0), 0);

    const tongChi = filteredItems
        .filter(item => item['Nghiệp vụ'] === 'Chi')
        .reduce((sum, item) => sum + (parseFloat(item['Số tiền']) || 0), 0);

    const soGiaoDich = filteredItems.length;

    // JSX Render
    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border border-gray-100">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <DollarSign className="w-7 h-7 text-indigo-600" />
                            Quản lý Thu Chi Theo Lô
                        </h1>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowCaiDatModal(true)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Settings className="w-4 h-4" />
                                Cài đặt
                            </button>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Filter className="w-4 h-4" />
                                {showFilters ? "Ẩn bộ lọc" : "Bộ lọc"}
                            </button>
                            <button
                                onClick={() => handleOpenModal()}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm thu chi
                            </button>
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="mb-6 space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo ID, nghiệp vụ, hạng mục, số lô, người giao..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {showFilters && (
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
                                    <input
                                        type="date"
                                        value={dateFilterFrom}
                                        onChange={(e) => setDateFilterFrom(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
                                    <input
                                        type="date"
                                        value={dateFilterTo}
                                        onChange={(e) => setDateFilterTo(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Lọc theo nghiệp vụ</label>
                                    <select
                                        value={nghiepVuFilter}
                                        onChange={(e) => setNghiepVuFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Tất cả</option>
                                        {uniqueNghiepVu.map(nv => (
                                            <option key={nv} value={nv}>{nv}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Lọc theo lô</label>
                                    <select
                                        value={loFilter}
                                        onChange={(e) => setLoFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Tất cả lô</option>
                                        {uniqueLo.map(lo => (
                                            <option key={lo} value={lo}>{lo}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={() => {
                                            setDateFilterFrom('');
                                            setDateFilterTo('');
                                            setNghiepVuFilter('');
                                            setLoFilter('');
                                            setSearch('');
                                        }}
                                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full"
                                    >
                                        Xóa bộ lọc
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Statistics cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <h3 className="text-sm text-blue-700 mb-1">Tổng giao dịch</h3>
                            <p className="text-2xl font-bold text-blue-800">{soGiaoDich}</p>
                        </div>

                        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                            <h3 className="text-sm text-green-700 mb-1">Tổng thu</h3>
                            <p className="text-2xl font-bold text-green-800">
                                {tongThu.toLocaleString('vi-VN')} đ
                            </p>
                        </div>

                        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                            <h3 className="text-sm text-red-700 mb-1">Tổng chi</h3>
                            <p className="text-2xl font-bold text-red-800">
                                {tongChi.toLocaleString('vi-VN')} đ
                            </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                            <h3 className="text-sm text-purple-700 mb-1">Chênh lệch</h3>
                            <p className={`text-2xl font-bold ${(tongThu - tongChi) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                                {(tongThu - tongChi).toLocaleString('vi-VN')} đ
                            </p>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto">
                        {filteredItems.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            onClick={() => requestSort('Ngày')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Ngày
                                                {sortConfig.key === 'Ngày' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('Nghiệp vụ')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Nghiệp vụ
                                                {sortConfig.key === 'Nghiệp vụ' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Hạng mục
                                        </th>
                                        <th
                                            onClick={() => requestSort('Số lô')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Số lô
                                                {sortConfig.key === 'Số lô' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('Số tiền')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Số tiền
                                                {sortConfig.key === 'Số tiền' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Người giao
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Hình ảnh
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ghi chú
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Thao tác
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredItems.map((item, index) => (
                                        <tr key={`${item['ID']}-${index}`} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-700">
                                                        {new Date(item['Ngày']).toLocaleDateString('vi-VN')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${item['Nghiệp vụ'] === 'Thu'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {item['Nghiệp vụ']}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-900">
                                                {item['Hạng mục']}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Package className="w-4 h-4 text-indigo-600" />
                                                    <span className="text-sm font-medium text-gray-900">{item['Số lô']}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className={`text-sm font-bold ${item['Nghiệp vụ'] === 'Thu' ? 'text-green-700' : 'text-red-700'
                                                    }`}>
                                                    {parseFloat(item['Số tiền']).toLocaleString('vi-VN')} đ
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <User className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-700">{item['Người giao']}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                {item['Hình ảnh UNC'] ? (
                                                    <button
                                                        onClick={() => handleViewImage(item['Hình ảnh UNC'])}
                                                        className="w-10 h-10 relative group"
                                                    >
                                                        <img
                                                            src={item['Hình ảnh UNC']}
                                                            alt="Thu chi"
                                                            className="w-full h-full object-cover rounded border border-gray-200 group-hover:opacity-75 transition-opacity"
                                                        />
                                                        <Eye className="absolute inset-0 m-auto w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </button>
                                                ) : (
                                                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                                                        <ImageIcon className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-700 max-w-xs truncate" title={item['Ghi chú']}>
                                                    {item['Ghi chú'] || '—'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleOpenModal(item)}
                                                        className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                        title="Sửa thu chi"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteConfirmation(item)}
                                                        className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                        title="Xóa thu chi"
                                                    >
                                                        <Trash className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-12 bg-white rounded-lg border">
                                <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <div className="text-gray-500 text-lg mb-2">Không tìm thấy dữ liệu</div>
                                <div className="text-sm text-gray-400">Không có giao dịch nào phù hợp với tiêu chí tìm kiếm</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditMode ? 'Cập nhật thu chi' : 'Thêm thu chi mới'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                                {/* ID - Hidden but shown for reference */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ID (tự động)
                                    </label>
                                    <input
                                        type="text"
                                        value={currentThuChi['ID']}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full bg-gray-100"
                                        readOnly
                                    />
                                </div>

                                {/* Ngày */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ngày <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formatDateForInput(currentThuChi['Ngày'])}
                                        onChange={(e) => handleInputChange('Ngày', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    />
                                </div>

                                {/* Nghiệp vụ */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nghiệp vụ <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={currentThuChi['Nghiệp vụ']}
                                        onChange={(e) => handleNghiepVuChange(e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    >
                                        <option value="">Chọn nghiệp vụ</option>
                                        {uniqueNghiepVu.map(nv => (
                                            <option key={nv} value={nv}>{nv}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Hạng mục */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hạng mục <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={currentThuChi['Hạng mục']}
                                        onChange={(e) => handleInputChange('Hạng mục', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                        disabled={!currentThuChi['Nghiệp vụ']}
                                    >
                                        <option value="">Chọn hạng mục</option>
                                        {currentThuChi['Nghiệp vụ'] && getHangMucByNghiepVu(currentThuChi['Nghiệp vụ']).map(hm => (
                                            <option key={hm} value={hm}>{hm}</option>
                                        ))}
                                    </select>
                                    {!currentThuChi['Nghiệp vụ'] && (
                                        <p className="text-xs text-gray-500 mt-1">Vui lòng chọn nghiệp vụ trước</p>
                                    )}
                                </div>

                                {/* Số lô - Searchable */}
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Số lô <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={searchLo}
                                            onChange={(e) => {
                                                setSearchLo(e.target.value);
                                                handleInputChange('Số lô', e.target.value);
                                                setShowLoDropdown(true);
                                            }}
                                            onFocus={() => setShowLoDropdown(true)}
                                            placeholder="Nhập hoặc chọn số lô"
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                        {showLoDropdown && filteredLo.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {filteredLo.map(lo => (
                                                    <div
                                                        key={lo['Số lô']}
                                                        onClick={() => handleLoSelect(lo['Số lô'])}
                                                        className="p-2 hover:bg-indigo-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {lo['Số lô']}
                                                        </div>
                                                        {lo['Nhà cung cấp'] && (
                                                            <div className="text-xs text-gray-500">
                                                                {lo['Nhà cung cấp']}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Số tiền - Formatted */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Số tiền <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={formatCurrency(currentThuChi['Số tiền'])}
                                            onChange={(e) => handleSoTienChange(e.target.value)}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500 pr-12"
                                            placeholder="0"
                                            required
                                        />
                                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                                            đ
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                                {/* Người giao */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Người giao <span className="text-red-500">*</span>
                                    </label>

                                    {/* Display selected person */}
                                    {currentThuChi['Người giao'] && (
                                        <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <User className="w-5 h-5 text-green-600" />
                                                <span className="font-medium text-green-800">
                                                    {currentThuChi['Người giao']}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleInputChange('Người giao', '')}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Search person */}
                                    {!currentThuChi['Người giao'] && (
                                        <>
                                            <div className="relative mb-2">
                                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Tìm kiếm nhân viên..."
                                                    value={searchNhanVien}
                                                    onChange={(e) => setSearchNhanVien(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                />
                                            </div>

                                            {/* Person List */}
                                            <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                                                <div className="space-y-2">
                                                    {filteredNhanVien.length > 0 ? (
                                                        filteredNhanVien.map(nv => (
                                                            <div
                                                                key={nv['Họ và Tên']}
                                                                onClick={() => {
                                                                    handleInputChange('Người giao', nv['Họ và Tên']);
                                                                    setSearchNhanVien('');
                                                                }}
                                                                className="p-2 hover:bg-indigo-50 rounded cursor-pointer transition-colors border border-transparent hover:border-indigo-200"
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {nv['Họ và Tên']}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {nv['Chức vụ']} - {nv['Phòng']}
                                                                        </div>
                                                                    </div>
                                                                    <Check className="w-5 h-5 text-indigo-600 opacity-0 hover:opacity-100" />
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="text-center py-4 text-gray-500">
                                                            {searchNhanVien ? 'Không tìm thấy nhân viên nào' : 'Không có nhân viên'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Ghi chú */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ghi chú
                                    </label>
                                    <textarea
                                        value={currentThuChi['Ghi chú']}
                                        onChange={(e) => handleInputChange('Ghi chú', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập ghi chú (nếu có)"
                                        rows="3"
                                    />
                                </div>

                                {/* Hình ảnh */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hình ảnh UNC
                                    </label>
                                    <div
                                        ref={dropZoneRef}
                                        onDragEnter={handleDragEnter}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`p-4 border-2 border-dashed rounded-lg flex flex-col items-center justify-center bg-gray-50 transition-all cursor-pointer hover:bg-gray-100 ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}
                                    >
                                        <div className="w-full flex items-center justify-center mb-3">
                                            <div className="w-24 h-24 border rounded-lg flex items-center justify-center bg-white overflow-hidden">
                                                {selectedImage ? (
                                                    <img
                                                        src={selectedImage}
                                                        alt="Preview"
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <ImageIcon className="h-10 w-10 text-gray-300" />
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <Upload className="h-5 w-5 mx-auto text-gray-400 mb-1" />
                                            <p className="text-sm text-gray-600 mb-1">
                                                {isDragging ? 'Thả ảnh vào đây' : 'Kéo và thả ảnh vào đây hoặc'}
                                            </p>
                                            <label className="cursor-pointer px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors shadow-sm inline-flex items-center">
                                                <Upload className="h-4 w-4 mr-2" />
                                                Chọn ảnh
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />
                                            </label>
                                        </div>

                                        <p className="text-xs text-gray-500 mt-3">
                                            Hình ảnh tối đa 5MB
                                        </p>
                                    </div>

                                    {selectedImage && (
                                        <div className="mt-2 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedImage(null);
                                                    handleInputChange('Hình ảnh UNC', '');
                                                }}
                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors shadow-sm flex items-center"
                                            >
                                                <X className="h-4 w-4 mr-1" />
                                                Xóa ảnh
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                disabled={isSubmitting}
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-indigo-600 text-white rounded-lg ${isSubmitting
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-indigo-700'
                                    } flex items-center gap-2 transition-colors shadow-sm`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        {isEditMode ? 'Cập nhật' : 'Thêm mới'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cài đặt Thu Chi Modal */}
            {showCaiDatModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Settings className="w-6 h-6 text-indigo-600" />
                                Cài đặt Thu Chi
                            </h2>
                            <button
                                onClick={handleCloseCaiDatModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Add new setting form */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                {isEditCaiDat ? 'Cập nhật cài đặt' : 'Thêm cài đặt mới'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nghiệp vụ <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={currentCaiDat['Nghiệp vụ']}
                                        onChange={(e) => setCurrentCaiDat({ ...currentCaiDat, 'Nghiệp vụ': e.target.value })}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                    // XÓA DÒNG NÀY: disabled={isEditCaiDat}
                                    >
                                        <option value="">Chọn nghiệp vụ</option>
                                        <option value="Thu">Thu</option>
                                        <option value="Chi">Chi</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hạng mục <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={currentCaiDat['Hạng mục']}
                                        onChange={(e) => setCurrentCaiDat({ ...currentCaiDat, 'Hạng mục': e.target.value })}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập hạng mục"
                                    // XÓA DÒNG NÀY: disabled={isEditCaiDat}
                                    />
                                </div>
                                <div className="flex items-end gap-2">
                                    <button
                                        onClick={handleSaveCaiDat}
                                        className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors shadow-sm flex-1"
                                    >
                                        <Check className="w-4 h-4" />
                                        {isEditCaiDat ? 'Cập nhật' : 'Thêm'}
                                    </button>
                                    {isEditCaiDat && (
                                        <button
                                            onClick={() => {
                                                setIsEditCaiDat(false);
                                                setCurrentCaiDat({ 'Nghiệp vụ': '', 'Hạng mục': '' });
                                            }}
                                            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Settings list */}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nghiệp vụ
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Hạng mục
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Thao tác
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {caiDatThuChiList.length > 0 ? (
                                        caiDatThuChiList.map((item, index) => (
                                            <tr key={`${item['Hạng mục']}-${index}`} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${item['Nghiệp vụ'] === 'Thu'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {item['Nghiệp vụ']}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                                                    {item['Hạng mục']}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-center">
                                                    <div className="flex justify-center space-x-2">
                                                        <button
                                                            onClick={() => handleOpenCaiDatModal(item)}
                                                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                            title="Sửa cài đặt"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenDeleteCaiDatConfirmation(item)}
                                                            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                            title="Xóa cài đặt"
                                                        >
                                                            <Trash className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                                                Chưa có cài đặt nào
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseCaiDatModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Delete Cài đặt Confirmation Modal */}
            {showDeleteCaiDatConfirmation && caiDatToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">Xác nhận xóa</h2>
                            <button
                                onClick={handleCloseDeleteCaiDatConfirmation}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                <p className="text-red-700">
                                    Bạn có chắc chắn muốn xóa cài đặt này?
                                </p>
                                <div className="mt-3 text-sm text-red-600">
                                    <p><strong>Nghiệp vụ:</strong> {caiDatToDelete['Nghiệp vụ']}</p>
                                    <p><strong>Hạng mục:</strong> {caiDatToDelete['Hạng mục']}</p>
                                </div>
                                <p className="text-sm text-red-600 mt-2">
                                    Hành động này không thể hoàn tác.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseDeleteCaiDatConfirmation}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDeleteCaiDat}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash className="h-4 w-4" />
                                    Xóa cài đặt
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && itemToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">Xác nhận xóa</h2>
                            <button
                                onClick={handleCloseDeleteConfirmation}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                <p className="text-red-700">
                                    Bạn có chắc chắn muốn xóa giao dịch này?
                                </p>
                                <div className="mt-3 text-sm text-red-600">
                                    <p><strong>ID:</strong> {itemToDelete['ID']}</p>
                                    <p><strong>Ngày:</strong> {new Date(itemToDelete['Ngày']).toLocaleDateString('vi-VN')}</p>
                                    <p><strong>Nghiệp vụ:</strong> {itemToDelete['Nghiệp vụ']}</p>
                                    <p><strong>Hạng mục:</strong> {itemToDelete['Hạng mục']}</p>
                                    <p><strong>Số lô:</strong> {itemToDelete['Số lô']}</p>
                                    <p><strong>Số tiền:</strong> {parseFloat(itemToDelete['Số tiền']).toLocaleString('vi-VN')} đ</p>
                                </div>
                                <p className="text-sm text-red-600 mt-2">
                                    Hành động này không thể hoàn tác.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseDeleteConfirmation}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash className="h-4 w-4" />
                                    Xóa giao dịch
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image View Modal */}
            {showImageModal && imageToView && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={handleCloseImageModal}>
                    <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={handleCloseImageModal}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300"
                        >
                            <X className="h-8 w-8" />
                        </button>
                        <img
                            src={imageToView}
                            alt="Thu chi"
                            className="max-w-full max-h-[85vh] object-contain rounded-lg"
                        />
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default ThuChiTheoLoManagement;

